import {
  FunctionsError,
  getFunctions,
  type HttpsCallable,
  httpsCallable,
  type HttpsCallableOptions,
} from 'firebase/functions';
import { z, ZodTuple, type ZodType, ZodUndefined, ZodVoid } from 'zod';

import {
  NotFoundError,
  PermissionDeniedError,
  UnauthenticatedError,
} from './errors';

const AppErrorDetailsZ = z.object({ code: z.string() });

export function errorDetailsCode(err: unknown) {
  if (!(err instanceof FunctionsError)) return undefined;

  return AppErrorDetailsZ.safeParse(err.details).data?.code;
}

export function callable<REQ extends ZodType, RES extends ZodType>(
  name: string,
  ReqZ: REQ,
  ResZ: RES,
  options?: HttpsCallableOptions,
) {
  const isTuple = ReqZ instanceof ZodTuple;
  const isVoid = !ReqZ || ReqZ instanceof ZodVoid;

  let lazyFn: HttpsCallable;

  return (async (
    ...args: REQ extends ZodTuple ? z.output<REQ> : [z.output<REQ>]
  ) => {
    const { error: reqError, data: payload } =
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (await ReqZ?.safeEncodeAsync((isTuple ? args : args[0]) as any)) ?? {};

    if (reqError)
      throw new FunctionsError(
        'invalid-argument',
        'Validation failed',
        reqError.issues,
      );

    if (!lazyFn) {
      lazyFn = httpsCallable(getFunctions(), name, options);
    }

    let response;
    try {
      response = isVoid ? await lazyFn() : await lazyFn(payload);
    } catch (err) {
      if (err instanceof FunctionsError) {
        switch (err.code) {
          case 'functions/unauthenticated':
            throw new UnauthenticatedError(err.message, err.details);

          case 'functions/permission-denied':
            throw new PermissionDeniedError(err.message, err.details);

          case 'functions/not-found':
            throw new NotFoundError(err.message, err.details);
        }

        // if (err.code.startsWith('functions/')) {
        //   throw new FunctionsError(
        //     err.code as FunctionsErrorCode,
        //     err.message,
        //     (err as FunctionsError).details,
        //   );
        // }
      }

      throw err;
    }

    if (!ResZ || ResZ instanceof ZodVoid || ResZ instanceof ZodUndefined) {
      if (response.data !== null) {
        console.warn(
          `Firebase function '${name}' empty response expected`,
          response.data,
        );
      }

      return;
    }

    const { error: resError, data: result } = await ResZ.safeParseAsync(
      response.data,
    );

    if (resError) {
      throw new FunctionsError('ok', 'Unexpected response', resError.issues);
    }

    return result;
  }) as REQ extends ZodTuple
    ? (...data: z.output<REQ>) => Promise<z.output<RES>>
    : REQ extends ZodVoid
      ? () => Promise<z.output<RES>>
      : (data: z.output<REQ>) => Promise<z.output<RES>>;
}
